import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './BigTournaments.css';

const API_ORIGIN =
  process.env.REACT_APP_API_URL ||
  'http://localhost:5000';

const API_BASE = `${API_ORIGIN}/api`;

const LOCALES = {
  ru: 'ru-RU',
  en: 'en-GB',
  ka: 'ka-GE',
  hy: 'hy-AM',
  az: 'az-AZ',
  it: 'it-IT',
  fr: 'fr-FR',
};

const PRIZE_TEMPLATES = [
  {
    minPlayers: 2,
    maxPlayers: 10,
    places: [
      { from: 1, to: 1, percent: 100 },
    ],
  },
  {
    minPlayers: 11,
    maxPlayers: 20,
    places: [
      { from: 1, to: 1, percent: 60 },
      { from: 2, to: 2, percent: 40 },
    ],
  },
  {
    minPlayers: 21,
    maxPlayers: 40,
    places: [
      { from: 1, to: 1, percent: 45 },
      { from: 2, to: 2, percent: 25 },
      { from: 3, to: 4, percent: 20 },
      { from: 5, to: 8, percent: 10 },
    ],
  },
  {
    minPlayers: 41,
    maxPlayers: Number.POSITIVE_INFINITY,
    places: [
      { from: 1, to: 1, percent: 30 },
      { from: 2, to: 2, percent: 20 },
      { from: 3, to: 4, percent: 20 },
      { from: 5, to: 8, percent: 16 },
      { from: 9, to: 16, percent: 14 },
    ],
  },
];

function round2(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function formatCreditsValue(value, locale) {
  const number = Number(value || 0);

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function formatDateValue(value, locale) {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString(locale, {
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCountdownValue(startAt, now, translate) {
  if (!startAt) return '';

  const target = new Date(startAt).getTime();
  const diff = target - now;

  if (diff <= 0) {
    return translate('bigTournaments.countdown.starting');
  }

  const totalSeconds = Math.floor(diff / 1000);

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor(
    (totalSeconds % 86400) / 3600
  );
  const minutes = Math.floor(
    (totalSeconds % 3600) / 60
  );
  const seconds =
    totalSeconds % 60;

  if (days > 0) {
    return translate('bigTournaments.countdown.days', {
      days,
      hours,
      minutes,
    });
  }

  if (hours > 0) {
    return translate('bigTournaments.countdown.hours', {
      hours,
      minutes,
      seconds,
    });
  }

  return translate('bigTournaments.countdown.minutes', {
    minutes,
    seconds: String(seconds).padStart(2, '0'),
  });
}

function toDateTimeLocal(date) {
  const pad = (value) => String(value).padStart(2, '0');

  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
}

function buildPrizeRules(minPlayers, maxPlayers) {
  const min = Math.max(2, Number(minPlayers || 4));
  const max = Math.max(min, Number(maxPlayers || 64));

  return PRIZE_TEMPLATES
    .map((template) => {
      const ruleMin = Math.max(min, template.minPlayers);

      const templateMax =
        Number.isFinite(template.maxPlayers)
          ? template.maxPlayers
          : max;

      const ruleMax = Math.min(max, templateMax);

      if (ruleMin > ruleMax) {
        return null;
      }

      return {
        minPlayers: ruleMin,
        maxPlayers: ruleMax,
        places: template.places.map((place) => ({
          ...place,
        })),
      };
    })
    .filter(Boolean);
}

function getClientTimeZone() {
  try {
    return (
      Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone ||
      'UTC'
    );
  } catch (_) {
    return 'UTC';
  }
}


function makeInitialForm() {
  const minPlayers = 4;
  const maxPlayers = 64;

  return {
    organizerType: 'user',
    name: '',
    // Тип нард должен быть выбран явно пользователем.
    // Не ставим LONG по умолчанию, чтобы случайно не создавать длинный турнир.
    gameType: '',

    // Ежедневная серия выключена по умолчанию.
    repeatDaily: false,
    repeatTimezone:
      getClientTimeZone(),

    mode: 'knockout',
    startAt: toDateTimeLocal(
      new Date(Date.now() + 15 * 60 * 1000)
    ),
    minPlayers,
    maxPlayers,
    buyIn: 0,
    commissionPercent: 0,
    guaranteedPrizePool: 0,

    rebuyDestination: 'organizer',
    rebuyCommissionPercent: 0,

    prizeRules: buildPrizeRules(
      minPlayers,
      maxPlayers
    ),
    rebuyConfig: [
      { round: 1, cost: 0 },
      { round: 2, cost: 0 },
      { round: 3, cost: 0 },
    ],
  };
}

export default function BigTournamentsPage() {
  const navigate = useNavigate();
  const { t: translate, i18n } = useTranslation();

  const languageCode = String(
    i18n.resolvedLanguage ||
    i18n.language ||
    'ru'
  )
    .split('-')[0]
    .toLowerCase();

  const locale =
    LOCALES[languageCode] ||
    LOCALES.ru;

  const statusLabels = {
    registration: translate('bigTournaments.statuses.registration'),
    starting: translate('bigTournaments.statuses.starting'),
    qualification: translate('bigTournaments.statuses.qualification'),
    bracket: translate('bigTournaments.statuses.bracket'),
    finished: translate('bigTournaments.statuses.finished'),
    cancelled: translate('bigTournaments.statuses.cancelled'),
    rebuy_round: translate('bigTournaments.statuses.rebuyRound'),
    rebuy_window: translate('bigTournaments.statuses.rebuyWindow'),
  };

  const formatCredits = (value) =>
    formatCreditsValue(value, locale);

  const formatDate = (value) =>
    formatDateValue(value, locale);

  const formatCountdown = (startAt, currentTime) =>
    formatCountdownValue(
      startAt,
      currentTime,
      translate
    );

  const [tournaments, setTournaments] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [now, setNow] =
    useState(Date.now());

  const [statusFilter, setStatusFilter] =
    useState('registration');

  const [permissionsLoading, setPermissionsLoading] =
    useState(true);

  const [canCreateAdmin, setCanCreateAdmin] =
    useState(false);

  const [canCreateClub, setCanCreateClub] =
    useState(false);

  const [myClub, setMyClub] =
    useState(null);

  const [createOpen, setCreateOpen] =
    useState(false);

  const [createLoading, setCreateLoading] =
    useState(false);

  const [createError, setCreateError] =
    useState('');

  const [seriesSavingId, setSeriesSavingId] =
    useState(null);

  const [seriesError, setSeriesError] =
    useState('');

  const [form, setForm] =
    useState(makeInitialForm);


  const token =
    localStorage.getItem('token') || '';

  const localRole =
    localStorage.getItem('role') || '';

  const localUsername =
    localStorage.getItem('username') ||
    translate('bigTournaments.common.player');

  const canCreatePersonal =
    Boolean(token);


  const loadPermissions = async () => {
    const admin =
      localRole === 'admin' ||
      localRole === 'superadmin';

    setCanCreateAdmin(admin);

    if (!token) {
      setCanCreateClub(false);
      setMyClub(null);
      setPermissionsLoading(false);
      return;
    }

    try {
      const response =
        await fetch(
          `${API_BASE}/clubs/my`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      const data =
        await response.json();

      if (
        response.ok &&
        data?.inClub &&
        data?.club
      ) {
        const allowed =
          data?.myRole === 'captain' ||
          Boolean(
            data?.myPermissions
              ?.canCreateClubTournaments
          );

        setCanCreateClub(
          allowed
        );

        setMyClub(
          data.club
        );
      } else {
        setCanCreateClub(false);
        setMyClub(null);
      }
    } catch (e) {
      console.warn(
        '[BIG TOURNAMENTS] club permissions:',
        e
      );

      setCanCreateClub(false);
      setMyClub(null);
    } finally {
      setPermissionsLoading(false);
    }
  };


  const loadTournaments = async () => {
    try {
      const response =
        await fetch(
          `${API_BASE}/big-tournaments`
        );

      const data =
        await response.json();

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.error ||
          translate('bigTournaments.list.loadFailed')
        );
      }

      setTournaments(
        Array.isArray(data.tournaments)
          ? data.tournaments
          : []
      );

      setError('');

    } catch (e) {
      console.error(
        '[BIG TOURNAMENTS PAGE]',
        e
      );

      setError(
        e.message ||
        translate('bigTournaments.list.loadError')
      );

    } finally {
      setLoading(false);
    }
  };


  const updateDailySeries =
    async (
      seriesId,
      enabled
    ) => {
      if (
        !seriesId ||
        !token
      ) {
        return;
      }

      try {
        setSeriesSavingId(
          Number(seriesId)
        );

        setSeriesError('');

        const response =
          await fetch(
            `${API_BASE}/big-tournaments/series/${seriesId}/repeat`,
            {
              method:
                'PATCH',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify({
                  enabled:
                    Boolean(enabled),
                }),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data?.ok
        ) {
          throw new Error(
            data?.error ||
            'Не удалось изменить ежедневный повтор'
          );
        }

        await loadTournaments();

      } catch (e) {
        console.error(
          '[BIG TOURNAMENT DAILY SERIES]',
          e
        );

        setSeriesError(
          e?.message ||
          'Не удалось изменить ежедневный повтор'
        );

      } finally {
        setSeriesSavingId(null);
      }
    };


  const canManageRepeatSeries =
    (tournament) => {
      if (canCreateAdmin) {
        return true;
      }

      if (
        tournament?.creator_type ===
          'club' &&
        canCreateClub &&
        myClub?.id &&
        Number(tournament.club_id) ===
          Number(myClub.id)
      ) {
        return true;
      }

      if (
        tournament?.creator_type ===
          'user'
      ) {
        const organizerName =
          String(
            tournament?.organizer?.name ||
            tournament?.organizer_name_snapshot ||
            ''
          );

        return (
          organizerName &&
          organizerName ===
            localUsername
        );
      }

      return false;
    };


  useEffect(() => {
    loadTournaments();
    loadPermissions();

    const poll =
      setInterval(
        loadTournaments,
        5000
      );

    const clock =
      setInterval(
        () => setNow(Date.now()),
        1000
      );

    return () => {
      clearInterval(poll);
      clearInterval(clock);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const sorted =
    useMemo(() => {
      const priority = {
        registration: 1,
        starting: 2,
        qualification: 3,
        bracket: 4,
        rebuy_round: 4,
        rebuy_window: 4,
        finished: 10,
        cancelled: 11,
      };

      return [...tournaments].sort(
        (a, b) => {
          const pa =
            priority[a.status] ?? 5;

          const pb =
            priority[b.status] ?? 5;

          if (pa !== pb) {
            return pa - pb;
          }

          return (
            new Date(a.start_at).getTime() -
            new Date(b.start_at).getTime()
          );
        }
      );
    }, [tournaments]);


  const filterCounts =
    useMemo(() => {
      const counts = {
        all: sorted.length,
        registration: 0,
        live: 0,
        finished: 0,
        cancelled: 0,
      };

      sorted.forEach((tournament) => {
        if (
          tournament.status ===
          'registration'
        ) {
          counts.registration += 1;
          return;
        }

        if (
          [
            'starting',
            'qualification',
            'bracket',
            'rebuy_round',
            'rebuy_window',
          ].includes(
            tournament.status
          )
        ) {
          counts.live += 1;
          return;
        }

        if (
          tournament.status ===
          'finished'
        ) {
          counts.finished += 1;
          return;
        }

        if (
          tournament.status ===
          'cancelled'
        ) {
          counts.cancelled += 1;
        }
      });

      return counts;
    }, [sorted]);


  const filteredSorted =
    useMemo(() => {
      if (
        statusFilter === 'all'
      ) {
        return sorted;
      }

      if (
        statusFilter ===
        'registration'
      ) {
        return sorted.filter(
          tournament =>
            tournament.status ===
            'registration'
        );
      }

      if (
        statusFilter === 'live'
      ) {
        return sorted.filter(
          tournament =>
            [
              'starting',
              'qualification',
              'bracket',
              'rebuy_round',
              'rebuy_window',
            ].includes(
              tournament.status
            )
        );
      }

      if (
        statusFilter ===
        'finished'
      ) {
        return sorted.filter(
          tournament =>
            tournament.status ===
            'finished'
        );
      }

      if (
        statusFilter ===
        'cancelled'
      ) {
        return sorted.filter(
          tournament =>
            tournament.status ===
            'cancelled'
        );
      }

      return sorted;
    }, [
      sorted,
      statusFilter,
    ]);


  const activeFilterLabel = {
    all: translate('bigTournaments.list.filters.all'),
    registration:
      translate('bigTournaments.list.filters.registration'),
    live: translate('bigTournaments.list.filters.live'),
    finished: translate('bigTournaments.list.filters.finished'),
    cancelled: translate('bigTournaments.list.filters.cancelled'),
  }[statusFilter] || translate('bigTournaments.list.sectionTitle');


  const repeatUi =
    languageCode === 'ru'
      ? {
          createLabel:
            'Повторять турнир ежедневно',

          createHelp:
            'После завершения или отмены из-за недобора сервер автоматически создаст следующий турнир на следующий день в это же локальное время. Повтор работает, пока галочка включена.',

          cardLabel:
            'Ежедневный повтор',

          saving:
            'Сохраняю...',

          errorPrefix:
            'Повтор временно не может создать следующий турнир:',
        }
      : {
          createLabel:
            'Repeat tournament every day',

          createHelp:
            'After the tournament finishes or is cancelled because of too few players, the server automatically creates the next tournament for the next day at the same local time. Repeats continue while this option is enabled.',

          cardLabel:
            'Daily repeat',

          saving:
            'Saving...',

          errorPrefix:
            'The repeat cannot create the next tournament yet:',
        };


  const organizerOptions = [
    ...(canCreateAdmin
      ? [
          {
            value: 'admin',
            label:
              'Backgammon Quest',
          },
        ]
      : []),

    ...(canCreateClub
      ? [
          {
            value: 'club',
            label:
              `CLUB ${myClub?.name || ''}`,
          },
        ]
      : []),

    ...(canCreatePersonal
      ? [
          {
            value: 'user',
            label:
              translate(
                'bigTournaments.create.personalOrganizer',
                { username: localUsername }
              ),
          },
        ]
      : []),
  ];


  const canCreate =
    organizerOptions.length > 0;


  const openCreateModal = () => {
    const fresh =
      makeInitialForm();

    if (canCreateAdmin) {
      fresh.organizerType =
        'admin';

    } else if (canCreateClub) {
      fresh.organizerType =
        'club';

    } else {
      fresh.organizerType =
        'user';
    }

    setForm(fresh);
    setCreateError('');
    setCreateOpen(true);
  };


  const closeCreateModal = () => {
    if (createLoading) {
      return;
    }

    setCreateOpen(false);
    setCreateError('');
  };


  const setField = (
    field,
    value
  ) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };


  const rebuildPrizeRules = () => {
    const min =
      Number(form.minPlayers);

    const max =
      Number(form.maxPlayers);

    if (
      !Number.isInteger(min) ||
      !Number.isInteger(max) ||
      min < 2 ||
      max < min
    ) {
      setCreateError(
        translate('bigTournaments.create.errors.playerRange')
      );

      return;
    }

    setCreateError('');

    setForm((previous) => ({
      ...previous,
      prizeRules:
        buildPrizeRules(
          min,
          max
        ),
    }));
  };


  const updatePrizeRule = (
    ruleIndex,
    field,
    value
  ) => {
    setForm((previous) => ({
      ...previous,

      prizeRules:
        previous.prizeRules.map(
          (rule, index) =>
            index === ruleIndex
              ? {
                  ...rule,
                  [field]: value,
                }
              : rule
        ),
    }));
  };


  const updatePrizePlace = (
    ruleIndex,
    placeIndex,
    field,
    value
  ) => {
    setForm((previous) => ({
      ...previous,

      prizeRules:
        previous.prizeRules.map(
          (rule, index) => {
            if (index !== ruleIndex) {
              return rule;
            }

            return {
              ...rule,

              places:
                rule.places.map(
                  (place, currentPlaceIndex) =>
                    currentPlaceIndex === placeIndex
                      ? {
                          ...place,
                          [field]: value,
                        }
                      : place
                ),
            };
          }
        ),
    }));
  };


  const addPrizePlace = (
    ruleIndex
  ) => {
    setForm((previous) => ({
      ...previous,

      prizeRules:
        previous.prizeRules.map(
          (rule, index) => {
            if (index !== ruleIndex) {
              return rule;
            }

            const last =
              rule.places[
                rule.places.length - 1
              ];

            const nextFrom =
              last
                ? Number(last.to) + 1
                : 1;

            return {
              ...rule,

              places: [
                ...rule.places,
                {
                  from: nextFrom,
                  to: nextFrom,
                  percent: 0,
                },
              ],
            };
          }
        ),
    }));
  };


  const removePrizePlace = (
    ruleIndex,
    placeIndex
  ) => {
    setForm((previous) => ({
      ...previous,

      prizeRules:
        previous.prizeRules.map(
          (rule, index) => {
            if (index !== ruleIndex) {
              return rule;
            }

            return {
              ...rule,

              places:
                rule.places.filter(
                  (_, currentPlaceIndex) =>
                    currentPlaceIndex !==
                    placeIndex
                ),
            };
          }
        ),
    }));
  };


  const addPrizeRule = () => {
    setForm((previous) => {
      const last =
        previous.prizeRules[
          previous.prizeRules.length - 1
        ];

      const minPlayers =
        last
          ? Number(last.maxPlayers) + 1
          : Number(previous.minPlayers);

      const maxPlayers =
        Math.max(
          minPlayers,
          Number(previous.maxPlayers)
        );

      return {
        ...previous,

        prizeRules: [
          ...previous.prizeRules,

          {
            minPlayers,
            maxPlayers,

            places: [
              {
                from: 1,
                to: 1,
                percent: 100,
              },
            ],
          },
        ],
      };
    });
  };


  const removePrizeRule = (
    ruleIndex
  ) => {
    setForm((previous) => ({
      ...previous,

      prizeRules:
        previous.prizeRules.filter(
          (_, index) =>
            index !== ruleIndex
        ),
    }));
  };


  const updateRebuyRow = (
    rowIndex,
    field,
    value
  ) => {
    setForm((previous) => ({
      ...previous,

      rebuyConfig:
        previous.rebuyConfig.map(
          (row, index) =>
            index === rowIndex
              ? {
                  ...row,
                  [field]: value,
                }
              : row
        ),
    }));
  };


  const addRebuyRow = () => {
    setForm((previous) => {
      const last =
        previous.rebuyConfig[
          previous.rebuyConfig.length - 1
        ];

      return {
        ...previous,

        rebuyConfig: [
          ...previous.rebuyConfig,

          {
            round:
              Number(
                last?.round || 0
              ) + 1,

            cost: 0,
          },
        ],
      };
    });
  };


  const removeRebuyRow = (
    rowIndex
  ) => {
    setForm((previous) => ({
      ...previous,

      rebuyConfig:
        previous.rebuyConfig.filter(
          (_, index) =>
            index !== rowIndex
        ),
    }));
  };


  const validateCreateForm = () => {
    if (
      !String(form.name || '').trim()
    ) {
      return translate('bigTournaments.create.errors.nameRequired');
    }

    // Тип игры обязателен. Никакого скрытого LONG по умолчанию.
    if (
      form.gameType !== 'long' &&
      form.gameType !== 'short'
    ) {
      return (
        `${translate('bigTournaments.create.gameType')}: ` +
        `${translate('bigTournaments.common.longBackgammon')} / ` +
        `${translate('bigTournaments.common.shortBackgammon')}`
      );
    }

    const startAt =
      new Date(form.startAt);

    if (
      Number.isNaN(startAt.getTime()) ||
      startAt.getTime() <= Date.now()
    ) {
      return translate('bigTournaments.create.errors.startFuture');
    }

    const minPlayers =
      Number(form.minPlayers);

    const maxPlayers =
      Number(form.maxPlayers);

    if (
      !Number.isInteger(minPlayers) ||
      minPlayers < 2
    ) {
      return translate('bigTournaments.create.errors.minPlayers');
    }

    if (
      !Number.isInteger(maxPlayers) ||
      maxPlayers < minPlayers
    ) {
      return translate('bigTournaments.create.errors.maxPlayers');
    }

    if (
      Number(form.buyIn) < 0 ||
      !Number.isFinite(
        Number(form.buyIn)
      )
    ) {
      return translate('bigTournaments.create.errors.invalidBuyIn');
    }

    const commission =
      Number(
        form.commissionPercent
      );

    if (
      !Number.isFinite(commission) ||
      commission < 0 ||
      commission > 100
    ) {
      return translate('bigTournaments.create.errors.commissionRange');
    }


    if (
  form.mode === 'rebuy' &&
  form.rebuyDestination === 'prize_pool'
) {
  const rebuyCommission =
    Number(
      form.rebuyCommissionPercent
    );

  if (
    !Number.isFinite(rebuyCommission) ||
    rebuyCommission < 0 ||
    rebuyCommission > 100
  ) {
    return translate('bigTournaments.create.errors.rebuyCommissionRange');
  }
}


   if (
  form.mode === 'rebuy' &&
  form.rebuyDestination === 'organizer' &&
  Number(
    form.guaranteedPrizePool
  ) <= 0
) {
  return translate('bigTournaments.create.errors.guaranteedRequired');
}

    if (
      !Array.isArray(form.prizeRules) ||
      form.prizeRules.length === 0
    ) {
      return translate('bigTournaments.create.errors.prizeRuleRequired');
    }

    for (
      let i = 0;
      i < form.prizeRules.length;
      i++
    ) {
      const rule =
        form.prizeRules[i];

      const total =
        round2(
          (rule.places || []).reduce(
            (sum, place) =>
              sum +
              Number(
                place.percent || 0
              ),
            0
          )
        );

      if (
        Math.abs(total - 100) > 0.01
      ) {
        return translate(
          'bigTournaments.create.errors.percentTotal',
          {
            min: rule.minPlayers,
            max: rule.maxPlayers,
            total,
          }
        );
      }
    }

    return '';
  };


  const createTournament = async () => {
    const validationError =
      validateCreateForm();

    if (validationError) {
      setCreateError(
        validationError
      );

      return;
    }

    if (!token) {
      setCreateError(
        translate('bigTournaments.create.errors.authRequired')
      );

      return;
    }

    const organizerType =
      form.organizerType;


    if (
      organizerType === 'admin' &&
      !canCreateAdmin
    ) {
      setCreateError(
        translate('bigTournaments.create.errors.adminPermission')
      );

      return;
    }


    if (
      organizerType === 'club' &&
      !canCreateClub
    ) {
      setCreateError(
        translate('bigTournaments.create.errors.clubPermission')
      );

      return;
    }


    if (
      organizerType === 'user' &&
      !canCreatePersonal
    ) {
      setCreateError(
        translate('bigTournaments.create.errors.personalAuth')
      );

      return;
    }


    try {
      setCreateLoading(true);
      setCreateError('');


      const endpoint =
        organizerType === 'club'
          ? `${API_BASE}/big-tournaments/club/create`
          : organizerType === 'user'
            ? `${API_BASE}/big-tournaments/user/create`
            : `${API_BASE}/big-tournaments/admin/create`;


      const safePrizeRules =
        form.prizeRules.map(
          (rule) => ({
            minPlayers:
              Number(
                rule.minPlayers
              ),

            maxPlayers:
              Number(
                rule.maxPlayers
              ),

            places:
              rule.places.map(
                (place) => ({
                  from:
                    Number(
                      place.from
                    ),

                  to:
                    Number(
                      place.to
                    ),

                  percent:
                    Number(
                      place.percent
                    ),
                })
              ),
          })
        );


const safeRebuyConfig =
  form.mode === 'rebuy'
    ? form.rebuyConfig
        .map(
          (row) => ({
            round:
              Number(
                row.round
              ),

            cost:
              Number(
                row.cost
              ),

            destination:
              form.rebuyDestination ===
              'prize_pool'
                ? 'prize_pool'
                : 'organizer',

            commissionPercent:
              form.rebuyDestination ===
              'prize_pool'
                ? Number(
                    form.rebuyCommissionPercent ||
                    0
                  )
                : 0,
          })
        )
              .filter(
                (row) =>
                  Number.isInteger(
                    row.round
                  ) &&
                  row.round > 0 &&
                  Number.isFinite(
                    row.cost
                  ) &&
                  row.cost > 0
              )
          : [];


      const payload = {
        name:
          String(form.name).trim(),

        // Отправляем оба варианта имени поля.
        // Новый сервер принимает оба, а это дополнительно защищает от рассинхронизации клиента/сервера.
        gameType:
          form.gameType,

        game_type:
          form.gameType,

        repeatDaily:
          Boolean(
            form.repeatDaily
          ),

        repeatTimezone:
          form.repeatTimezone ||
          getClientTimeZone(),

        mode:
          form.mode,

        startAt:
          new Date(
            form.startAt
          ).toISOString(),

        minPlayers:
          Number(
            form.minPlayers
          ),

        maxPlayers:
          Number(
            form.maxPlayers
          ),

        buyIn:
          Number(
            form.buyIn || 0
          ),

        commissionPercent:
          Number(
            form.commissionPercent || 0
          ),

     prizePoolType:
  form.mode === 'rebuy' &&
  form.rebuyDestination === 'organizer'
    ? 'guaranteed'
    : 'dynamic',

guaranteedPrizePool:
  form.mode === 'rebuy' &&
  form.rebuyDestination === 'organizer'
    ? Number(
        form.guaranteedPrizePool || 0
      )
    : 0,

        rebuyConfig:
          safeRebuyConfig,

        prizeRules:
          safePrizeRules,
      };


      const response =
        await fetch(
          endpoint,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Authorization:
                `Bearer ${token}`,
            },

            body:
              JSON.stringify(
                payload
              ),
          }
        );


      const data =
        await response.json();


      if (
        !response.ok ||
        !data?.ok
      ) {
        throw new Error(
          data?.error ||
          translate('bigTournaments.create.errors.createFailed')
        );
      }


      if (
        data.balance !==
        undefined &&
        data.balance !==
        null
      ) {
        window.dispatchEvent(
          new CustomEvent(
            'balance-updated',
            {
              detail: {
                balance:
                  Number(
                    data.balance
                  ),

                source:
                  'big-tournament-guarantee',

                allowDecrease: true,
              },
            }
          )
        );
      }


      setCreateOpen(false);
      setForm(
        makeInitialForm()
      );

      await loadTournaments();


    } catch (e) {
      console.error(
        '[BIG TOURNAMENT CREATE]',
        e
      );

      setCreateError(
        e.message ||
        translate('bigTournaments.create.errors.createFailed')
      );

    } finally {
      setCreateLoading(false);
    }
  };


  return (
    <div className="big-tournaments-page">

      <section className="big-tournaments-hero">

        <div className="big-tournaments-hero__eyebrow">
          BACKGAMMON QUEST
        </div>

        <h1>
          {translate('bigTournaments.list.title')}
        </h1>

      <p>
        {translate('bigTournaments.list.subtitle')}
      </p>

      </section>


      <section className="big-tournaments-content">

        <div className="big-tournaments-heading">

          <div>
            <h2>{translate('bigTournaments.list.sectionTitle')}</h2>

            <span>
              {activeFilterLabel}:
              {' '}
              {filteredSorted.length}
              {' '}
              {translate('bigTournaments.list.of')}
              {' '}
              {sorted.length}
            </span>
          </div>


          <div className="big-tournaments-heading__actions">

            {!permissionsLoading &&
              canCreate && (
              <button
                type="button"
                className="big-tournaments-create"
                onClick={
                  openCreateModal
                }
              >
                {translate('bigTournaments.list.createTournament')}
              </button>
            )}


            <button
              type="button"
              className="big-tournaments-refresh"
              onClick={
                loadTournaments
              }
            >
              {translate('bigTournaments.list.refresh')}
            </button>

          </div>

        </div>


        <div
          className="big-tournaments-filters"
          role="tablist"
          aria-label={translate('bigTournaments.list.filterAria')}
        >

          <button
            type="button"
            role="tab"
            aria-selected={
              statusFilter ===
              'registration'
            }
            className={`
              big-tournaments-filter
              ${statusFilter === 'registration'
                ? 'is-active'
                : ''}
            `}
            onClick={() =>
              setStatusFilter(
                'registration'
              )
            }
          >
            <span>
              {translate('bigTournaments.list.filters.registrationShort')}
            </span>

            <strong>
              {filterCounts.registration}
            </strong>
          </button>


          <button
            type="button"
            role="tab"
            aria-selected={
              statusFilter === 'live'
            }
            className={`
              big-tournaments-filter
              ${statusFilter === 'live'
                ? 'is-active'
                : ''}
            `}
            onClick={() =>
              setStatusFilter('live')
            }
          >
            <span>
              {translate('bigTournaments.list.filters.live')}
            </span>

            <strong>
              {filterCounts.live}
            </strong>
          </button>


          <button
            type="button"
            role="tab"
            aria-selected={
              statusFilter ===
              'finished'
            }
            className={`
              big-tournaments-filter
              ${statusFilter === 'finished'
                ? 'is-active'
                : ''}
            `}
            onClick={() =>
              setStatusFilter(
                'finished'
              )
            }
          >
            <span>
              {translate('bigTournaments.list.filters.finished')}
            </span>

            <strong>
              {filterCounts.finished}
            </strong>
          </button>


          {filterCounts.cancelled > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={
                statusFilter ===
                'cancelled'
              }
              className={`
                big-tournaments-filter
                ${statusFilter === 'cancelled'
                  ? 'is-active'
                  : ''}
              `}
              onClick={() =>
                setStatusFilter(
                  'cancelled'
                )
              }
            >
              <span>
                {translate('bigTournaments.list.filters.cancelled')}
              </span>

              <strong>
                {filterCounts.cancelled}
              </strong>
            </button>
          )}


          <button
            type="button"
            role="tab"
            aria-selected={
              statusFilter === 'all'
            }
            className={`
              big-tournaments-filter
              ${statusFilter === 'all'
                ? 'is-active'
                : ''}
            `}
            onClick={() =>
              setStatusFilter('all')
            }
          >
            <span>
              {translate('bigTournaments.list.filters.allShort')}
            </span>

            <strong>
              {filterCounts.all}
            </strong>
          </button>

        </div>


        {seriesError && (
          <div className="big-tournaments-state big-tournaments-state--error">
            {seriesError}
          </div>
        )}


        {loading && (
          <div className="big-tournaments-state">
            {translate('bigTournaments.list.loading')}
          </div>
        )}


        {!loading && error && (
          <div className="big-tournaments-state big-tournaments-state--error">
            {error}
          </div>
        )}


        {!loading &&
          !error &&
          filteredSorted.length === 0 && (
            <div className="big-tournaments-state">
              {statusFilter === 'registration'
                ? translate('bigTournaments.list.empty.registration')
                : statusFilter === 'live'
                  ? translate('bigTournaments.list.empty.live')
                  : statusFilter === 'finished'
                    ? translate('bigTournaments.list.empty.finished')
                    : statusFilter === 'cancelled'
                      ? translate('bigTournaments.list.empty.cancelled')
                      : translate('bigTournaments.list.empty.all')}
            </div>
          )}


        <div className="big-tournaments-grid">

          {filteredSorted.map((tournament) => {

            const registered =
              Number(
                tournament.registeredCount || 0
              );

            const maxPlayers =
              tournament.max_players;

            const progress =
              maxPlayers
                ? Math.min(
                    100,
                    Math.round(
                      registered /
                      Number(maxPlayers) *
                      100
                    )
                  )
                : 0;

            const isRegistration =
              tournament.status ===
              'registration';

            const isFinished =
              tournament.status ===
              'finished';

            const organizer =
              tournament.organizer || {};

            const organizerName =
              organizer.name ||
              tournament.organizer_name_snapshot ||
              (
                tournament.creator_type === 'club'
                  ? translate('bigTournaments.common.club')
                  : tournament.creator_type === 'user'
                    ? translate('bigTournaments.common.player')
                    : 'Backgammon Quest'
              );

            const poolType =
              tournament.prize_pool_type ||
              'dynamic';

            const prizePool =
              poolType === 'guaranteed'
                ? Number(
                    tournament.guaranteed_prize_pool ||
                    tournament.current_prize_pool ||
                    0
                  )
                : Number(
                    tournament.current_prize_pool ||
                    0
                  );

            const prizePlaces =
              Number(
                tournament.prizePlaces || 0
              );


            return (
              <article
                key={tournament.id}
                className={`
                  big-tournament-card
                  big-tournament-card--${tournament.status}
                `}
              >

                <div className="big-tournament-card__top">

                  <span
                    className={`
                      big-tournament-status
                      big-tournament-status--${tournament.status}
                    `}
                  >
                    {statusLabels[
                      tournament.status
                    ] || tournament.status}
                  </span>

                  <span className="big-tournament-id">
                    #{tournament.id}
                  </span>

                </div>


                <h3>
                  {tournament.name}
                </h3>


                <div className="big-tournament-organizer">

                  <span className="big-tournament-organizer__label">
                    {translate('bigTournaments.common.organizer')}
                  </span>

                  <strong>
                    {tournament.creator_type === 'club'
                      ? `CLUB ${organizerName}`
                      : organizerName}
                  </strong>

                </div>


               <div className="big-tournament-type">

  <span>
    {tournament.game_type === 'short'
      ? translate('bigTournaments.common.shortBackgammon')
      : translate('bigTournaments.common.longBackgammon')}
  </span>

                  <span className="big-tournament-dot">
                    •
                  </span>

                  <span>
                    {tournament.mode ===
                    'rebuy'
                      ? 'Rebuy'
                      : 'Knockout'}
                  </span>

                </div>


                {tournament.repeatSeries && (
                  <div
                    style={{
                      margin:
                        '10px 0 12px',

                      padding:
                        '10px 12px',

                      border:
                        '1px solid rgba(255,255,255,.10)',

                      borderRadius:
                        12,

                      background:
                        'rgba(255,255,255,.035)',
                    }}
                  >
                    <label
                      style={{
                        display:
                          'flex',

                        alignItems:
                          'center',

                        gap:
                          9,

                        cursor:
                          canManageRepeatSeries(
                            tournament
                          )
                            ? 'pointer'
                            : 'default',

                        fontWeight:
                          700,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          Boolean(
                            tournament
                              .repeatSeries
                              .enabled
                          )
                        }
                        disabled={
                          !canManageRepeatSeries(
                            tournament
                          ) ||
                          Number(
                            seriesSavingId
                          ) ===
                            Number(
                              tournament
                                .repeatSeries
                                .id
                            )
                        }
                        onChange={
                          (event) =>
                            updateDailySeries(
                              tournament
                                .repeatSeries
                                .id,

                              event.target
                                .checked
                            )
                        }
                      />

                      <span>
                        {repeatUi.cardLabel}
                      </span>

                      {Number(
                        seriesSavingId
                      ) ===
                        Number(
                          tournament
                            .repeatSeries
                            .id
                        ) && (
                        <small
                          style={{
                            opacity:
                              .7,
                          }}
                        >
                          {repeatUi.saving}
                        </small>
                      )}
                    </label>

                    {tournament
                      .repeatSeries
                      .lastError && (
                      <div
                        style={{
                          marginTop:
                            7,

                          fontSize:
                            12,

                          lineHeight:
                            1.35,

                          color:
                            '#ffb4b4',
                        }}
                      >
                        {repeatUi.errorPrefix}
                        {' '}
                        {tournament
                          .repeatSeries
                          .lastError}
                      </div>
                    )}
                  </div>
                )}


                <div className="big-tournament-card__info">

                  <div>
                    <span className="big-tournament-info-label">
                      {translate('bigTournaments.common.start')}
                    </span>

                    <strong>
                      {formatDate(
                        tournament.start_at
                      )}
                    </strong>
                  </div>


                  <div>
                    <span className="big-tournament-info-label">
                      {translate('bigTournaments.common.participants')}
                    </span>

                    <strong>
                      {registered}
                      {' / '}
                      {maxPlayers || '∞'}
                    </strong>
                  </div>

                </div>


                <div className="big-tournament-prize-box">

                  <div>
                    <span>
                      {poolType === 'guaranteed'
                        ? translate('bigTournaments.common.guaranteedFund')
                        : translate('bigTournaments.common.currentPrizePool')}
                    </span>

                    <strong>
                      {formatCredits(
                        prizePool
                      )}
                      {' '}
                      <small>
                        $
                      </small>
                    </strong>
                  </div>


                  <div>
                    <span>
                      {translate('bigTournaments.common.buyIn')}
                    </span>

                    <strong>
                      {Number(
                        tournament.buy_in || 0
                      ) > 0
                        ? (
                          <>
                            {formatCredits(
                              tournament.buy_in
                            )}
                            {' '}
                            <small>
                              $
                            </small>
                          </>
                        )
                        : translate('bigTournaments.common.free')}
                    </strong>
                  </div>


                  <div>
                    <span>
                      {translate('bigTournaments.common.prizePlaces')}
                    </span>

                    <strong>
                      {prizePlaces || '—'}
                    </strong>
                  </div>

                </div>


                <div className="big-tournament-progress">

                  <div className="big-tournament-progress__track">
                    <span
                      style={{
                        width:
                          `${progress}%`
                      }}
                    />
                  </div>

                </div>


                {isRegistration && (
                  <div className="big-tournament-countdown">

                    <span>
                      {translate('bigTournaments.common.untilStart')}
                    </span>

                    <strong>
                      {formatCountdown(
                        tournament.start_at,
                        now
                      )}
                    </strong>

                  </div>
                )}


                {isFinished && (
                  <div className="big-tournament-completed">
                    {translate('bigTournaments.list.tournamentFinished')}
                  </div>
                )}


                <button
                  type="button"
                  className="big-tournament-open"
                  onClick={() =>
                    navigate(
                      `/big-tournaments/${tournament.id}`
                    )
                  }
                >
                  {isRegistration
                    ? translate('bigTournaments.list.detailsAndRegister')
                    : translate('bigTournaments.list.openTournament')}
                </button>

              </article>
            );
          })}

        </div>

      </section>


      {createOpen && (
        <div
          className="big-tournament-create-overlay"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeCreateModal();
            }
          }}
        >

          <div className="big-tournament-create-modal">

            <div className="big-tournament-create-modal__head">

              <div>
                <span>
                  BIG TOURNAMENT
                </span>

                <h2>
                  {translate('bigTournaments.create.title')}
                </h2>
              </div>


              <button
                type="button"
                className="big-tournament-create-close"
                onClick={
                  closeCreateModal
                }
              >
                ×
              </button>

            </div>


            <div className="big-tournament-create-modal__body">

              {createError && (
                <div className="big-tournament-create-error">
                  {createError}
                </div>
              )}


              <div className="big-tournament-create-intro">
                <div className="big-tournament-create-intro__icon">
                  ?
                </div>

                <div>
                  <strong>
                    {translate('bigTournaments.create.introLead')}
                  </strong>

                  <p>
                    {translate('bigTournaments.create.introText')}
                  </p>
                </div>
              </div>


              <div className="big-tournament-create-layout">

                <div className="big-tournament-create-main">

              <section className="big-tournament-create-section">

                <h3>
                  {translate('bigTournaments.create.basicSettings')}
                </h3>


                {organizerOptions.length > 1 && (
                  <label className="big-tournament-form-field">

                    <span>
                      {translate('bigTournaments.common.organizer')}
                    </span>

                    <select
                      value={
                        form.organizerType
                      }
                      onChange={(event) =>
                        setField(
                          'organizerType',
                          event.target.value
                        )
                      }
                    >
                      {organizerOptions.map(
                        (option) => (
                          <option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        )
                      )}
                    </select>

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.organizerHelp')}
                    </small>

                  </label>
                )}


                {organizerOptions.length === 1 && (
                  <div className="big-tournament-organizer-preview">

                    <span>
                      {translate('bigTournaments.common.organizer')}
                    </span>

                    <strong>
                      {organizerOptions[0]?.label || '—'}
                    </strong>

                  </div>
                )}


                <div className="big-tournament-form-grid">

                  <label className="big-tournament-form-field big-tournament-form-field--wide">

                    <span>
                      {translate('bigTournaments.create.name')}
                    </span>

                    <input
                      type="text"
                      maxLength={100}
                      value={form.name}
                      onChange={(event) =>
                        setField(
                          'name',
                          event.target.value
                        )
                      }
                      placeholder={translate('bigTournaments.create.namePlaceholder')}
                    />

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.nameHelp')}
                    </small>

                  </label>


                                 <label className="big-tournament-form-field">

                    <span>
                      {translate('bigTournaments.create.gameType')}
                    </span>

                    <select
                      value={form.gameType}
                      required
                      onChange={(event) =>
                        setField(
                          'gameType',
                          event.target.value
                        )
                      }
                    >
                      <option value="" disabled>
                        {translate('bigTournaments.create.gameType')}
                      </option>

                      <option value="long">
                        {translate('bigTournaments.common.longBackgammon')}
                      </option>

                      <option value="short">
                        {translate('bigTournaments.common.shortBackgammon')}
                      </option>
                    </select>

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.gameTypeHelp')}
                    </small>

                  </label>

                  <label className="big-tournament-form-field">

                    <span>
                      {translate('bigTournaments.common.format')}
                    </span>

                    <select
                      value={form.mode}
                      onChange={(event) =>
                        setField(
                          'mode',
                          event.target.value
                        )
                      }
                    >
                      <option value="knockout">
                        Knockout
                      </option>

                      <option value="rebuy">
                        Rebuy
                      </option>
                    </select>

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.formatHelp')}
                    </small>

                  </label>

                  <label className="big-tournament-form-field">

                    <span>
                      {translate('bigTournaments.common.start')}
                    </span>

                    <input
                      type="datetime-local"
                      value={
                        form.startAt
                      }
                      onChange={(event) =>
                        setField(
                          'startAt',
                          event.target.value
                        )
                      }
                    />

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.startHelp')}
                    </small>

                  </label>


                  <label className="big-tournament-form-field">

                    <span>
                      {translate('bigTournaments.common.minPlayers')}
                    </span>

                    <input
                      type="number"
                      min="2"
                      step="1"
                      value={
                        form.minPlayers
                      }
                      onChange={(event) =>
                        setField(
                          'minPlayers',
                          event.target.value
                        )
                      }
                    />

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.minPlayersHelp')}
                    </small>

                  </label>


                  <label className="big-tournament-form-field">

                    <span>
                      {translate('bigTournaments.common.maxPlayers')}
                    </span>

                    <input
                      type="number"
                      min="2"
                      step="1"
                      value={
                        form.maxPlayers
                      }
                      onChange={(event) =>
                        setField(
                          'maxPlayers',
                          event.target.value
                        )
                      }
                    />

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.maxPlayersHelp')}
                    </small>

                  </label>


                  <label className="big-tournament-form-field">

                    <span>
                      {translate('bigTournaments.create.buyInLabel')}
                    </span>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        form.buyIn
                      }
                      onChange={(event) =>
                        setField(
                          'buyIn',
                          event.target.value
                        )
                      }
                    />

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.buyInHelp')}
                    </small>

                  </label>


                  <label className="big-tournament-form-field">

                    <span>
                      {translate('bigTournaments.create.commissionLabel')}
                    </span>

                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={
                        form.commissionPercent
                      }
                      onChange={(event) =>
                        setField(
                          'commissionPercent',
                          event.target.value
                        )
                      }
                    />

                    <small className="big-tournament-field-help">
                      {translate('bigTournaments.create.commissionHelp')}
                    </small>

                  </label>

                </div>


                <label
                  className="big-tournament-form-field big-tournament-form-field--wide"
                  style={{
                    marginTop:
                      12,

                    padding:
                      '12px 14px',

                    border:
                      '1px solid rgba(255,255,255,.10)',

                    borderRadius:
                      12,

                    background:
                      'rgba(255,255,255,.035)',
                  }}
                >
                  <span
                    style={{
                      display:
                        'flex',

                      alignItems:
                        'center',

                      gap:
                        10,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        Boolean(
                          form.repeatDaily
                        )
                      }
                      onChange={
                        (event) =>
                          setField(
                            'repeatDaily',
                            event.target.checked
                          )
                      }
                    />

                    <strong>
                      {repeatUi.createLabel}
                    </strong>
                  </span>

                  <small className="big-tournament-field-help">
                    {repeatUi.createHelp}
                    {' '}
                    [{form.repeatTimezone}]
                  </small>
                </label>


                <button
                  type="button"
                  className="big-tournament-rules-rebuild"
                  onClick={
                    rebuildPrizeRules
                  }
                >
                  {translate('bigTournaments.create.recalculatePrizeRanges')}
                </button>

              </section>


              <section className="big-tournament-create-section">

                <h3>
                  {translate('bigTournaments.common.prizePool')}
                </h3>


                {(
  form.mode === 'knockout' ||
  (
    form.mode === 'rebuy' &&
    form.rebuyDestination === 'prize_pool'
  )
) ? (

                  <div className="big-tournament-pool-explanation">

                    <strong>
                      {translate('bigTournaments.create.dynamicFund')}
                    </strong>

                   <span>
  {form.mode === 'rebuy'
    ? translate(
        'bigTournaments.create.dynamicFundRebuyDescription'
      )
    : translate(
        'bigTournaments.create.dynamicFundDescription'
      )}
</span>

                  </div>

                ) : (

                  <>
                    <div className="big-tournament-pool-explanation">

                      <strong>
                        {translate('bigTournaments.common.guaranteedFund')}
                      </strong>

                      <span>
                        {translate('bigTournaments.create.guaranteedFundHelp')}
                      </span>

                    </div>


                    <label className="big-tournament-form-field big-tournament-guarantee-field">

                      <span>
                        {translate('bigTournaments.create.guaranteedFundLabel')}
                      </span>

                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={
                          form.guaranteedPrizePool
                        }
                        onChange={(event) =>
                          setField(
                            'guaranteedPrizePool',
                            event.target.value
                          )
                        }
                      />

                      <small className="big-tournament-field-help">
                        {translate('bigTournaments.create.guaranteedFundExample')}
                      </small>

                    </label>

                  </>
                )}

              </section>


              {form.mode === 'rebuy' && (
                <section className="big-tournament-create-section">

                  <div className="big-tournament-section-heading-row">

                    <h3>
                      {translate('bigTournaments.create.rebuyRounds')}
                    </h3>

                    <button
                      type="button"
                      onClick={
                        addRebuyRow
                      }
                    >
                      {translate('bigTournaments.create.addRound')}
                    </button>

                  </div>

<div className="big-tournament-form-grid">

  <label className="big-tournament-form-field">

    <span>
      {translate('bigTournaments.create.rebuyDestination')}
    </span>

    <select
      value={
        form.rebuyDestination
      }
      onChange={(event) =>
        setField(
          'rebuyDestination',
          event.target.value
        )
      }
    >
      <option value="organizer">
        {translate('bigTournaments.create.toOrganizer')}
      </option>

      <option value="prize_pool">
        {translate('bigTournaments.create.toPrizePool')}
      </option>
    </select>

    <small className="big-tournament-field-help">
      {translate('bigTournaments.create.rebuyDestinationHelp')}
    </small>

  </label>


  {form.rebuyDestination ===
    'prize_pool' && (

    <label className="big-tournament-form-field">

      <span>
        {translate('bigTournaments.create.rebuyCommission')}
      </span>

      <input
        type="number"
        min="0"
        max="100"
        step="0.01"
        value={
          form.rebuyCommissionPercent
        }
        onChange={(event) =>
          setField(
            'rebuyCommissionPercent',
            event.target.value
          )
        }
      />

      <small className="big-tournament-field-help">
        {translate('bigTournaments.create.rebuyCommissionHelp')}
      </small>

    </label>
  )}

</div>
                  <div className="big-tournament-simple-note">
                    <strong>
                      {translate('bigTournaments.create.howRebuyWorks')}
                    </strong>

                    <p>
                      {translate('bigTournaments.create.rebuyHelp')}
                    </p>
                  </div>


                  <div className="big-tournament-rebuy-list">

                    {form.rebuyConfig.map(
                      (row, rowIndex) => (

                        <div
                          key={rowIndex}
                          className="big-tournament-rebuy-row"
                        >

                          <label>
                            <span>
                              {translate('bigTournaments.common.round')}
                            </span>

                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={
                                row.round
                              }
                              onChange={(event) =>
                                updateRebuyRow(
                                  rowIndex,
                                  'round',
                                  event.target.value
                                )
                              }
                            />
                          </label>


                          <label>
                            <span>
                              {translate('bigTournaments.common.cost')}
                            </span>

                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={
                                row.cost
                              }
                              onChange={(event) =>
                                updateRebuyRow(
                                  rowIndex,
                                  'cost',
                                  event.target.value
                                )
                              }
                            />
                          </label>


                          <button
                            type="button"
                            className="big-tournament-small-remove"
                            onClick={() =>
                              removeRebuyRow(
                                rowIndex
                              )
                            }
                          >
                            {translate('bigTournaments.common.delete')}
                          </button>

                        </div>
                      )
                    )}

                  </div>

                </section>
              )}


              <section className="big-tournament-create-section">

                <div className="big-tournament-section-heading-row">

                  <div>
                    <h3>
                      {translate('bigTournaments.create.prizeRules')}
                    </h3>

                    <p>
                      {translate('bigTournaments.create.prizeRulesHelp')}
                    </p>
                  </div>


                  <button
                    type="button"
                    onClick={
                      addPrizeRule
                    }
                  >
                    {translate('bigTournaments.create.addRange')}
                  </button>

                </div>


                <div className="big-tournament-prize-rules-editor">

                  {form.prizeRules.map(
                    (rule, ruleIndex) => {

                      const totalPercent =
                        round2(
                          (rule.places || [])
                            .reduce(
                              (sum, place) =>
                                sum +
                                Number(
                                  place.percent || 0
                                ),
                              0
                            )
                        );


                      return (
                        <div
                          key={ruleIndex}
                          className="big-tournament-prize-rule-editor"
                        >

                          <div className="big-tournament-prize-rule-editor__head">

                            <div className="big-tournament-rule-player-range">

                              <strong>
                                {translate('bigTournaments.create.when')}
                              </strong>

                              <input
                                type="number"
                                min="2"
                                step="1"
                                value={
                                  rule.minPlayers
                                }
                                onChange={(event) =>
                                  updatePrizeRule(
                                    ruleIndex,
                                    'minPlayers',
                                    event.target.value
                                  )
                                }
                              />

                              <span>
                                –
                              </span>

                              <input
                                type="number"
                                min="2"
                                step="1"
                                value={
                                  rule.maxPlayers
                                }
                                onChange={(event) =>
                                  updatePrizeRule(
                                    ruleIndex,
                                    'maxPlayers',
                                    event.target.value
                                  )
                                }
                              />

                              <strong>
                                {translate('bigTournaments.create.playersSuffix')}
                              </strong>

                            </div>


                            <button
                              type="button"
                              className="big-tournament-small-remove"
                              onClick={() =>
                                removePrizeRule(
                                  ruleIndex
                                )
                              }
                            >
                              {translate('bigTournaments.create.deleteRange')}
                            </button>

                          </div>


                          <div className="big-tournament-prize-place-headings">
                            <span>
                              {translate('bigTournaments.common.places')}
                            </span>

                            <span>
                              {translate('bigTournaments.create.fundPercent')}
                            </span>

                            <span />
                          </div>


                          <div className="big-tournament-prize-place-list">

                            {rule.places.map(
                              (
                                place,
                                placeIndex
                              ) => (

                                <div
                                  key={placeIndex}
                                  className="big-tournament-prize-place-row"
                                >

                                  <div className="big-tournament-place-range">

                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={
                                        place.from
                                      }
                                      onChange={(event) =>
                                        updatePrizePlace(
                                          ruleIndex,
                                          placeIndex,
                                          'from',
                                          event.target.value
                                        )
                                      }
                                    />

                                    <span>
                                      –
                                    </span>

                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={
                                        place.to
                                      }
                                      onChange={(event) =>
                                        updatePrizePlace(
                                          ruleIndex,
                                          placeIndex,
                                          'to',
                                          event.target.value
                                        )
                                      }
                                    />

                                  </div>


                                  <div className="big-tournament-percent-input">

                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                      value={
                                        place.percent
                                      }
                                      onChange={(event) =>
                                        updatePrizePlace(
                                          ruleIndex,
                                          placeIndex,
                                          'percent',
                                          event.target.value
                                        )
                                      }
                                    />

                                    <span>
                                      %
                                    </span>

                                  </div>


                                  <button
                                    type="button"
                                    className="big-tournament-small-remove"
                                    onClick={() =>
                                      removePrizePlace(
                                        ruleIndex,
                                        placeIndex
                                      )
                                    }
                                  >
                                    ×
                                  </button>

                                </div>
                              )
                            )}

                          </div>


                          <div className="big-tournament-prize-rule-editor__footer">

                            <button
                              type="button"
                              className="big-tournament-add-place"
                              onClick={() =>
                                addPrizePlace(
                                  ruleIndex
                                )
                              }
                            >
                              {translate('bigTournaments.create.addPrizeGroup')}
                            </button>


                            <div
                              className={`
                                big-tournament-rule-total
                                ${Math.abs(totalPercent - 100) <= 0.01
                                  ? 'ok'
                                  : 'bad'}
                              `}
                            >
                              {translate('bigTournaments.common.total')}:
                              {' '}
                              <strong>
                                {totalPercent}%
                              </strong>
                            </div>

                          </div>

                        </div>
                      );
                    }
                  )}

                </div>

              </section>

                </div>


                <aside className="big-tournament-create-help">

                  <div className="big-tournament-create-help__sticky">

                    <div className="big-tournament-help-title">
                      <span>
                        {translate('bigTournaments.create.help')}
                      </span>

                      <h3>
                        {translate('bigTournaments.create.howToCreate')}
                      </h3>

                      <p>
                        {translate('bigTournaments.create.helpIntro')}
                      </p>
                    </div>


                    <div className="big-tournament-help-steps">

                      <div>
                        <strong>1</strong>
                        <p>
                          <b>{translate('bigTournaments.create.helpStep1Title')}</b>
                          {' '}
                          {translate('bigTournaments.create.helpStep1Text')}
                        </p>
                      </div>

                      <div>
                        <strong>2</strong>
                        <p>
                          <b>{translate('bigTournaments.create.helpStep2Title')}</b>
                          {' '}
                          {translate('bigTournaments.create.helpStep2Text')}
                        </p>
                      </div>

                      <div>
                        <strong>3</strong>
                        <p>
                          <b>{translate('bigTournaments.create.helpStep3Title')}</b>
                          {' '}
                          {translate('bigTournaments.create.helpStep3Text')}
                        </p>
                      </div>

                    </div>


                    <div className="big-tournament-help-summary">

                      <span className="big-tournament-help-summary__title">
                        {translate('bigTournaments.create.currentTournament')}
                      </span>

                      <div>
                        <span>{translate('bigTournaments.common.format')}</span>
                        <strong>
                          {form.mode === 'rebuy'
                            ? 'Rebuy'
                            : 'Knockout'}
                        </strong>
                      </div>

                      <div>
                        <span>{translate('bigTournaments.common.players')}</span>
                        <strong>
                          {form.minPlayers || '—'}
                          {' – '}
                          {form.maxPlayers || '—'}
                        </strong>
                      </div>

                      <div>
                        <span>{translate('bigTournaments.common.buyIn')}</span>
                        <strong>
                          {Number(form.buyIn || 0) > 0
                            ? `${formatCredits(form.buyIn)} $`
                            : translate('bigTournaments.common.free')}
                        </strong>
                      </div>

                      <div>
                        <span>{translate('bigTournaments.common.commission')}</span>
                        <strong>
                          {formatCredits(
                            form.commissionPercent || 0
                          )}
                          %
                        </strong>
                      </div>

                     <div>
  <span>{translate('bigTournaments.common.fund')}</span>
  <strong>
    {(
      form.mode === 'rebuy' &&
      form.rebuyDestination === 'organizer'
    )
      ? `${formatCredits(
          form.guaranteedPrizePool || 0
        )} $`
      : translate('bigTournaments.common.dynamic')}
  </strong>
</div>

                      <div>
                        <span>{translate('bigTournaments.create.prizeRulesCount')}</span>
                        <strong>
                          {form.prizeRules.length}
                        </strong>
                      </div>

                    </div>


                    <details className="big-tournament-help-details">
                      <summary>
                        {translate('bigTournaments.create.whatIsKnockout')}
                      </summary>

                      <p>
                        {translate('bigTournaments.create.knockoutExplanation')}
                      </p>
                    </details>


                    <details className="big-tournament-help-details">
                      <summary>
                        {translate('bigTournaments.create.whatIsRebuy')}
                      </summary>

                      <p>
                        {translate('bigTournaments.create.rebuyExplanation')}
                      </p>
                    </details>


                    <details className="big-tournament-help-details">
                      <summary>
                        {translate('bigTournaments.create.whatArePrizeRanges')}
                      </summary>

                      <p>
                        {translate('bigTournaments.create.prizeRangesExplanation')}
                      </p>
                    </details>


                    <div className="big-tournament-help-safe">
                      <strong>
                        {translate('bigTournaments.create.firstTournament')}
                      </strong>

                      <p>
                        {translate('bigTournaments.create.firstTournamentTip')}
                      </p>
                    </div>

                  </div>

                </aside>

              </div>

            </div>


            <div className="big-tournament-create-modal__footer">

              <button
                type="button"
                className="big-tournament-create-cancel"
                onClick={
                  closeCreateModal
                }
                disabled={
                  createLoading
                }
              >
                {translate('bigTournaments.common.cancel')}
              </button>


              <button
                type="button"
                className="big-tournament-create-submit"
                onClick={
                  createTournament
                }
                disabled={
                  createLoading
                }
              >
                {createLoading
                  ? translate('bigTournaments.create.creating')
                  : translate('bigTournaments.create.submit')}
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}
